import { useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { closeSnackbar } from "notistack";
import { CircularProgress } from "@mui/material";
import { useQueryClient } from "react-query";
import { useIntercom } from "react-use-intercom";

import OTPField from "../../Components/Form Fields/otp-field";
import PrimaryButton from "../../Components/Buttons/primary-button";

import { useSendVerificationCodeUsingUniqueID } from "../../CustomHooks/Queries";
import { useLoginWith2FACode, useRequestLogin2FACode } from "../../CustomHooks/Mutations";

import { useAppUtilityContext } from "../../Contexts/AppUtilityContext";

import {
  dispatchErrorToast,
  dispatchSuccessToast,
  onSignOut,
  transformCountryCodeFormatToUSNumber,
} from "../../Utils/helpers";
import { debugProps } from "../../Utils/constants";

import { Close } from "../../Assets/Icons";

export default function LoginWithUniqueContactID() {
  const resendOtpRef = useRef(false);

  const { show: showIntercom } = useIntercom();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { onUserCredentialsUpdate } = useAppUtilityContext();

  const formProps = useForm();

  const uniqueContactID = useMemo(() => new URLSearchParams(window.location.search).get("uniqueContactId"), []);

  const redirectTo = useMemo(() => {
    const redirectTo = new URLSearchParams(window.location.search).get("redirectTo");
    return (redirectTo ?? "").length === 0 ? "/login" : redirectTo;
  }, []);

  const {
    isFetching: isSendingVerificationCodeUsingUniqueID,
    data: sendVerificationCodeResponse,
    error: sendVerificationCodeError,
    isError: isSendVerificationCodeError,
    isSuccess: isSendVerificationCodeSuccess,
  } = useSendVerificationCodeUsingUniqueID(uniqueContactID, { enabled: !!uniqueContactID });

  const {
    handleSubmit,
    getValues,
    formState: { isValid },
  } = formProps;

  const userName = sendVerificationCodeResponse?.data?.phoneNumber;

  const formattedPhoneNumber = useMemo(() => transformCountryCodeFormatToUSNumber(userName ?? ""), [userName]);

  const { isLoading: isRequestingLogin2FACode, mutate: RequestLogin2FACode } = useRequestLogin2FACode({
    onSuccess: () => {
      dispatchSuccessToast(`New OTP code was sent at ${formattedPhoneNumber}`, { key: "request2FA" });
    },
    onError: () => {
      dispatchErrorToast("Oops! Something went wrong!", { key: "request2FA" });
    },
    onSettled: () => (resendOtpRef.current = false),
  });

  const { isLoading: isLoggingInWith2FACode, mutate: LoginWith2FACode } = useLoginWith2FACode({
    onSuccess: (data) => {
      onSignOut({ redirectOnLogout: false });
      queryClient.resetQueries();
      onUserCredentialsUpdate(data);
      dispatchSuccessToast("Verification success");
      navigate(redirectTo);
    },
    onError: (err) =>
      dispatchErrorToast(err?.response?.data?.error?.msg, {
        key: "loginWith2FA",
      }),
  });

  const isLoading = isRequestingLogin2FACode || isLoggingInWith2FACode;

  const onSubmit = () => {
    closeSnackbar();

    let payload = { ...debugProps, phoneNumber: userName };

    if (resendOtpRef.current) {
      !isLoading && RequestLogin2FACode(payload);
    } else {
      payload.phoneNumberCode = getValues("otp");
      !isLoading && LoginWith2FACode(payload);
    }
  };

  const onFormError = (errors) => {
    dispatchErrorToast(errors.otp.message, { key: "otp" });
  };

  return (
    <div
      className={`onboarding-page login-with-unique-id justify-content-${
        isSendingVerificationCodeUsingUniqueID
          ? "center"
          : isSendVerificationCodeError || !uniqueContactID
          ? "between"
          : "sm-center justify-content-start"
      }`}
    >
      {isSendingVerificationCodeUsingUniqueID && <CircularProgress sx={{ color: "var(--mist)" }} />}
      {!isSendingVerificationCodeUsingUniqueID && (
        <>
          {(isSendVerificationCodeError || !uniqueContactID) && (
            <>
              <div className="max-width-container justify-content-between">
                <div className="dummy-header" />
                <div className="status-container">
                  <Close className="status-icon" />
                  <div className="error-title mt-6">
                    {isSendVerificationCodeError
                      ? sendVerificationCodeError?.response?.data?.message ?? "Network Error"
                      : "Link Invalid"}
                  </div>
                </div>
                <Link className="primary-button text-decoration-none my-6" to="/login">
                  Return to Login
                </Link>
              </div>
            </>
          )}
          {isSendVerificationCodeSuccess && (
            <>
              <FormProvider {...formProps}>
                <form className="form-card" onSubmit={handleSubmit(onSubmit, onFormError)} autoComplete="off">
                  <div className="card-header">
                    <h1 className="text-4xl-semibold color--mist mt-1 mb-0">Phone Number Verification</h1>
                    <p className="text-base-regular color--mist mt-2 mb-0">
                      Enter the 6-digit verification code sent to{" "}
                      <span className="username-badge">{formattedPhoneNumber}</span>
                    </p>
                  </div>
                  <div className="otp-field">
                    <OTPField />
                    <button
                      className="resend-otp-button mt-3"
                      onClick={() => {
                        resendOtpRef.current = true;
                        onSubmit();
                      }}
                      type="button"
                    >
                      Didn’t receive a text? Resend code
                    </button>
                  </div>
                  <PrimaryButton className="w-100" type="submit" disabled={!isValid || isLoading}>
                    Next {isLoading && <CircularProgress />}
                  </PrimaryButton>
                </form>
              </FormProvider>
              <button className="help-button" onClick={() => showIntercom()}>
                Trouble logging in? Contact Support
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}
